import { BoxBase, ButtonBase, CardBase, StackBase, TypographyBase } from '@fmc-digital/ui';
import { Link, useMediaQuery, useTheme } from '@mui/material';
import { currencyFormat } from '@utils/currency.format';
import { dateFormat } from '@utils/dateFormat';
import React, { useEffect, useId, useMemo, useState } from 'react';
import { config } from '../../../../config';
import { LoanDeatils } from '../../atoms/loanDetails/LoanDetails';
import { selectActiveAccountInformation } from '@store/selectors/accounts.selector';
import { useDispatch, useSelector } from 'react-redux';
import { AccountTemplate, PayDetails } from '@types';
import { AmplifyLogs, CodingConstants } from '@constants';
import { selectPayment } from '@store/selectors/payHistory.selector';
import moment from 'moment/moment';
import { useHomeContext } from '../../../context/MyHomeContext';
import { get } from 'lodash';
import { logUserEvent } from '@analytics/amplifyLogging';
import { selectRecurringPayment } from '@store/selectors/autoPay.selector';
import { fetchStatementAsync } from '@store/actions/statements.action';
import { selectLoanNumberSelected } from '@store/selectors/widgets.selectors';
import { useVWOContext } from 'context/VWOContext';

export const PaymentCard = ({ handleDocument, selectedAccount }: any) => {
    const dispatch = useDispatch();
    const recurringPay = useSelector(selectRecurringPayment);
    const { RecordVWOAttributes } = useVWOContext();
    const selectActiveAccount: AccountTemplate | undefined = useSelector(selectActiveAccountInformation);
    const payHistoryInfo: PayDetails = useSelector(selectPayment);
    const loanNumber = useSelector(selectLoanNumberSelected);
    const [autopaymentOption, setAutopaymentoption] = useState(false);
    const [paymentOption, setPaymentoption] = useState(true);
    const [viewStatmentOption, setViewstatementoption] = useState(true);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
    const {
        nextPaymentDate,
        accountStatus,
        totalDue,
        loanBalance,
        escrowBalance,
        interestRate,
        maturityDate,
        totalAmountDue,
        planNextDueAmount,
        planNextDueDate
    } = get({ selectActiveAccount }, ['selectActiveAccount'], {
        nextPaymentDate: '',
        accountStatus: { name: '', nameKey: '' },
        totalDue: 0,
        loanBalance: 0,
        escrowBalance: 0,
        interestRate: '',
        maturityDate: '',
        totalAmountDue: 0,
        planNextDueAmount: 0,
        planNextDueDate: ''
    });
    const {
        currentAccount,
        pastDue,
        paymentDueNow,
        delinquent,
        severelyDelinquent,
        severelyDelinquentWithRP,
        delinquentWithRP,
        showPlannedDueStatusList,
        monthlyStmt,
        showDashStatusList,
        disablepaynowStatusKeyList,
        enableautoPayStatusList,
        disableStatementStatusList
    } = CodingConstants;

    const { accountDetailSSO } = useHomeContext();
    const isPastDue = useMemo(() => accountStatus?.name === pastDue, [pastDue, accountStatus]);
    const isDelinquent = useMemo(() => accountStatus?.name === delinquent, [delinquent, accountStatus]);
    const isSeverelyDelinquent = useMemo(() => accountStatus?.name === severelyDelinquent, [severelyDelinquent, accountStatus]);
    const isSeverelyDelinquentWithRP = useMemo(
        () => accountStatus?.name === severelyDelinquentWithRP,
        [severelyDelinquentWithRP, accountStatus]
    );
    const isDelinquentWithRP = useMemo(() => accountStatus?.name === delinquentWithRP, [delinquentWithRP, accountStatus]);
    const isCurrent = useMemo(() => accountStatus?.name === currentAccount, [currentAccount, accountStatus]);
    const showDashes = useMemo(() => showDashStatusList.includes(accountStatus?.nameKey), [showDashStatusList, accountStatus]);
    const isPaymentDueNow = useMemo(() => accountStatus?.name === paymentDueNow, [paymentDueNow, accountStatus]);
    const accountStatusName = accountStatus?.name;
    const accountStatusNameKey = accountStatus?.nameKey;
    const isPlannedNextDue = useMemo(
        () => showPlannedDueStatusList.includes(accountStatus?.nameKey),
        [accountStatus?.nameKey, showPlannedDueStatusList]
    );

    const id = useId();

    const loanBaseUrl = config.env('myLoanBaseUrl');

    const getFormattedValue = () => {
        if (showDashes) {
            return '--';
        } else if (isCurrent) {
            return currencyFormat(totalDue);
        } else if (isPlannedNextDue) {
            return currencyFormat(Number(planNextDueAmount));
        } else {
            return currencyFormat(totalAmountDue);
        }
    };

    const { action, paymentHistoryLink, viewStatementLink, autoPayLink, accountDetailAction, accountDetailDescription } =
        AmplifyLogs.paynowWidget;

    const links = [
        {
            id: 'autoPay',
            text: 'Set up Autopay',
            link: `${loanBaseUrl}/LoanService/SSOPost?target_menu=recurringPayments`,
            action: autoPayLink,
            description: 'Set Up AutoPay button was clicked',
            show: enableautoPayStatusList.includes(accountStatus?.name) ? !recurringPay : true,
            disable: !autopaymentOption
        },
        {
            id: 'payHistory',
            text: 'View Payment History',
            link: `${loanBaseUrl}/LoanService/SSOPost?target_menu=paymentHistory`,
            action: paymentHistoryLink,
            description: 'View Payment History button was clicked',
            disable: false
        },
        {
            id: 'statement',
            text: 'View Current Statement',
            link: `${loanBaseUrl}/LoanService/SSOPost?target_menu=statements`,
            action: viewStatementLink,
            description: 'View Current Statement button was clicked',
            disable: !viewStatmentOption
        }
    ];

    const loanDeatils = [
        {
            title: 'Escrow Balance: ',
            value: currencyFormat(escrowBalance)
        },
        {
            title: 'Interest Rate: ',
            value: `${interestRate}%`
        },
        {
            title: 'Maturing: ',
            value: maturityDate
        }
    ];
    const onCurrentStatementClicked = () => {
        dispatch(fetchStatementAsync.request(loanNumber, ['2654', '2285']));
    };
    useEffect(() => {
        RecordVWOAttributes({ hasrecurringsetup: !!recurringPay });
    }, [RecordVWOAttributes, recurringPay]);

    useEffect(() => {
        const statusActions = [
            { list: disablepaynowStatusKeyList, action: (val: boolean) => setPaymentoption(!val) },
            { list: enableautoPayStatusList, action: (val: boolean) => setAutopaymentoption(val) },
            { list: disableStatementStatusList, action: (val: boolean) => setViewstatementoption(!val) }
        ];
        statusActions.forEach(({ list, action }) => {
            action(list.includes(accountStatusName) || list.includes(accountStatusNameKey));
        });
    }, [loanNumber, accountStatus]);

    return (
        <CardBase data-testid="paycard-container" sx={{ padding: `0 !important` }}>
            <StackBase
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
                padding={{ xs: theme.spacing(2), md: theme.spacing(4) }}
                data-testid="upcoming-payment"
            >
                <StackBase direction="column" alignItems="flex-start">
                    <StackBase>
                        <TypographyBase
                            variant="alt-subtitle1"
                            color={
                                isPastDue || isDelinquent || isSeverelyDelinquent || isSeverelyDelinquentWithRP || isDelinquentWithRP
                                    ? 'error.dark'
                                    : 'textPrimary'
                            }
                        >
                            {isPastDue ? 'Payment Past Due' : isCurrent || isPaymentDueNow ? 'Upcoming payment' : 'Total Due'}
                        </TypographyBase>
                        <TypographyBase
                            variant="alt-title1"
                            color={
                                isPastDue || isDelinquent || isSeverelyDelinquent || isSeverelyDelinquentWithRP || isDelinquentWithRP
                                    ? 'error.main'
                                    : 'primary.main'
                            }
                        >
                            {getFormattedValue()}
                        </TypographyBase>
                    </StackBase>
                    <StackBase>
                        <TypographyBase variant="paragraph-bold">Payment Due Date</TypographyBase>
                        <TypographyBase
                            variant="paragraph"
                            color={
                                isPastDue || isDelinquent || isSeverelyDelinquent || isSeverelyDelinquentWithRP || isDelinquentWithRP
                                    ? 'error.dark'
                                    : theme.palette.neutrals[400]
                            }
                        >
                            {showDashes ? '--' : isPlannedNextDue ? `${dateFormat(planNextDueDate)}` : `${dateFormat(nextPaymentDate)}`}
                        </TypographyBase>
                    </StackBase>
                </StackBase>

                <StackBase direction="column" alignItems="center" spacing={theme.spacing(2)} mt={{ xs: theme.spacing(2), lg: 0 }}>
                    <ButtonBase
                        onClick={() => {
                            logUserEvent({ type: 'PaymentCard', action, description: action });
                            accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost?target_menu=oneTimePayment`);
                        }}
                        color="primary"
                        variant="contained"
                        size="large"
                        disabled={!paymentOption}
                        fullWidth={useMediaQuery(theme.breakpoints.down('lg'))}
                    >
                        Pay now
                    </ButtonBase>
                    <StackBase direction="column" alignItems="center">
                        {links.map(
                            ({ text, link, id, description, action, show = true, disable }) =>
                                show && (
                                    <ButtonBase
                                        size="small"
                                        color="primary"
                                        variant="text"
                                        sx={{
                                            textTransform: 'none !important'
                                        }}
                                        key={id}
                                        disabled={disable}
                                        onClick={() => {
                                            logUserEvent({
                                                type: 'PaymentCard',
                                                action: action,
                                                description: description,
                                                loanNumber: selectedAccount?.accountNumber
                                            });
                                            if (id !== 'statement') accountDetailSSO(link);
                                            else onCurrentStatementClicked();
                                        }}
                                    >
                                        {text}
                                    </ButtonBase>
                                )
                        )}
                    </StackBase>
                </StackBase>
            </StackBase>
            <BoxBase sx={{ borderBottom: `1px solid ${theme.palette.surfaceDark.light}` }} />
            <StackBase direction={{ xs: 'column', lg: 'row' }}>
                <StackBase
                    padding={{ xs: theme.spacing(2), lg: theme.spacing(4) }}
                    direction={'column'}
                    spacing={theme.spacing(1)}
                    data-testid="current-loan-balance"
                    minWidth={{ lg: '19.84375rem' }}
                >
                    <StackBase>
                        <TypographyBase variant="alt-subtitle2">Current Loan Balance</TypographyBase>
                        <TypographyBase variant="title1" color="primary.main">
                            {currencyFormat(loanBalance)}
                        </TypographyBase>
                    </StackBase>
                    <StackBase spacing={theme.spacing(0.5)}>
                        {loanDeatils.map(details => (
                            <LoanDeatils key={id} {...details} />
                        ))}
                    </StackBase>
                </StackBase>
                {isMobile ? <BoxBase sx={{ borderRight: { xs: undefined, lg: `1px solid ${theme.palette.surfaceDark.light}` } }} /> : null}
                <StackBase
                    padding={{ xs: theme.spacing(2), lg: theme.spacing(4) }}
                    direction={'column'}
                    data-testid="last-payment"
                    pt={theme.spacing(4)}
                >
                    <StackBase spacing={theme.spacing(0.5)}>
                        <TypographyBase variant="alt-subtitle2">Last Payment</TypographyBase>
                        <TypographyBase variant="paragraph-bold">
                            {showDashes || !payHistoryInfo ? '--' : `${moment(payHistoryInfo?.PaymentDate).format('MM/DD/YYYY')}`}
                        </TypographyBase>
                        {payHistoryInfo && (
                            <TypographyBase variant="caption" color={theme.palette.surface.dark}>{`${currencyFormat(
                                payHistoryInfo?.Total
                            )} ${String(payHistoryInfo?.Description).toLowerCase()} was applied.`}</TypographyBase>
                        )}
                    </StackBase>
                    <StackBase>
                        <Link
                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                            onClick={() => {
                                logUserEvent({ type: 'PaymentCard', action: accountDetailAction, description: accountDetailDescription });
                                accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`);
                            }}
                        >
                            <TypographyBase variant="caption-bold" color="primary.main">
                                View Account Details
                            </TypographyBase>
                        </Link>
                    </StackBase>
                </StackBase>
            </StackBase>
        </CardBase>
    );
};
